import type { ReactElement } from 'react'

import { Icon, PillLink } from '@maria-cavalheiro-website/shared-ui-web'

import { ABOUT_PATH } from '../../../app/routes.ts'

export interface HeroSectionProps {
  readonly headingId: string
  /** `Olá` until the sky feature replaces it after mount. */
  readonly greeting: string
  /** `--:--` until the sky feature replaces it after mount. */
  readonly clock: string
  /** Absent while the clock is still a placeholder — `datetime="--:--"` is invalid. */
  readonly clockDateTime?: string
}

const EYEBROW = 'Poesia · desde sempre'
const TITLE = 'Poemas das coisas simples: flores, pássaros, o sol.'
const LEAD =
  'Nasci no Rio Grande do Sul e hoje moro em São Paulo. Escrevo desde sempre, entre flores, pássaros e gatos.'
const PRIMARY_CTA = 'Ler os poemas'
const SECONDARY_CTA = 'Sobre mim'

/**
 * The hero. The `h1` is the LCP element and must stay so — it is plain text in
 * prerendered HTML with a preloaded font, so the only way to break it is to put an
 * image above it. Do not.
 *
 * The staggered entrance animations are four modifier classes, not four inline
 * `animation-delay` declarations: `style-src 'self'` blocks the latter. They use
 * `animation-fill-mode: both`, which matters — the `rise` keyframe starts at
 * `opacity: 0`, and under the reduced-motion override the duration is cut to
 * 0.01ms. With `both` the element still lands at its `to` state; with `forwards`
 * alone some engines leave it invisible.
 */
export function HeroSection({
  headingId,
  greeting,
  clock,
  clockDateTime,
}: HeroSectionProps): ReactElement {
  return (
    <div className="hero">
      <p className="hero__eyebrow hero__rise hero__rise--1">
        <span className="hero__eyebrow-mark">
          <Icon name="feather" size={18} decorative />
        </span>
        <span className="hero__eyebrow-text">{EYEBROW}</span>
        <span className="hero__moment">
          <span className="hero__moment-dot" aria-hidden="true" />
          <span className="hero__greeting">{greeting}</span>
          <time className="hero__clock" dateTime={clockDateTime}>
            {clock}
          </time>
        </span>
      </p>

      <h1 className="hero__title hero__rise hero__rise--2" id={headingId}>
        {TITLE}
      </h1>

      <p className="hero__lead hero__rise hero__rise--3">{LEAD}</p>

      <div className="hero__actions hero__rise hero__rise--4">
        <PillLink variant="primary" href="#poemas" iconAfter="arrow-right">
          {PRIMARY_CTA}
        </PillLink>
        {/* A path, not `#sobre`: the biography is a route of its own now, and an
            anchor to a section this document no longer has would scroll nowhere. */}
        <PillLink variant="secondary" href={ABOUT_PATH}>
          {SECONDARY_CTA}
        </PillLink>
      </div>
    </div>
  )
}
